import { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import type { TTextilePurchase } from "@/api/customs/textiles";
import ExcelJS from "exceljs";

interface PaymentMethodPivotTableProps {
  data: TTextilePurchase[];
}

interface PivotRow {
  payment_method: string;
  revenue_con_iva: number;
  revenue_sin_iva: number;
}

export const PaymentMethodPivotTable = ({
  data,
}: PaymentMethodPivotTableProps) => {
  // Agrupar datos por método de pago
  const pivotData = useMemo(() => {
    const grouped = data.reduce((acc, item) => {
      if (!acc[item.payment_method]) {
        acc[item.payment_method] = {
          payment_method: item.payment_method,
          revenue_con_iva: 0,
          revenue_sin_iva: 0,
        };
      }

      acc[item.payment_method].revenue_con_iva += item.revenue_con_iva;
      acc[item.payment_method].revenue_sin_iva += item.revenue_sin_iva;

      return acc;
    }, {} as Record<string, PivotRow>);

    // Convertir a array y ordenar alfabéticamente por método de pago
    return Object.values(grouped).sort((a, b) =>
      a.payment_method.localeCompare(b.payment_method)
    );
  }, [data]);

  // Calcular totales
  const totals = useMemo(() => {
    return pivotData.reduce(
      (acc, row) => ({
        revenue_con_iva: acc.revenue_con_iva + row.revenue_con_iva,
        revenue_sin_iva: acc.revenue_sin_iva + row.revenue_sin_iva,
      }),
      { revenue_con_iva: 0, revenue_sin_iva: 0 }
    );
  }, [pivotData]);

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Pivot Métodos de Pago");

    // Definir columnas
    worksheet.columns = [
      { header: "MÉTODO DE PAGO", key: "payment_method", width: 30 },
      { header: "REVENUE CON IVA", key: "revenue_con_iva", width: 20 },
      { header: "REVENUE SIN IVA", key: "revenue_sin_iva", width: 20 },
    ];

    // Estilo para encabezados
    const headerRow = worksheet.getRow(1);
    headerRow.height = 25;
    
    // Aplicar estilo solo a las celdas con datos (columnas 1, 2, 3)
    [1, 2, 3].forEach((colNum) => {
      const cell = headerRow.getCell(colNum);
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1976D2" },
      };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Agregar filas de datos
    pivotData.forEach((row) => {
      const excelRow = worksheet.addRow({
        payment_method: row.payment_method,
        revenue_con_iva: row.revenue_con_iva,
        revenue_sin_iva: row.revenue_sin_iva,
      });

      // Aplicar formato, alineación y bordes solo a las celdas con datos
      [1, 2, 3].forEach((colNum) => {
        const cell = excelRow.getCell(colNum);
        
        if (colNum >= 2) {
          cell.numFmt = "#,##0";
          cell.alignment = { horizontal: "right" };
        }
        
        cell.border = {
          top: { style: "thin", color: { argb: "FFCCCCCC" } },
          left: { style: "thin", color: { argb: "FFCCCCCC" } },
          bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
          right: { style: "thin", color: { argb: "FFCCCCCC" } },
        };
      });
    });

    // Agregar fila de totales
    const totalRow = worksheet.addRow({
      payment_method: "TOTAL",
      revenue_con_iva: totals.revenue_con_iva,
      revenue_sin_iva: totals.revenue_sin_iva,
    });

    totalRow.height = 25;
    
    // Aplicar estilo solo a las celdas con datos (columnas 1, 2, 3)
    [1, 2, 3].forEach((colNum) => {
      const cell = totalRow.getCell(colNum);
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1976D2" },
      };
      
      if (colNum >= 2) {
        cell.numFmt = "#,##0";
        cell.alignment = { horizontal: "right" };
      }
      
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Descargar el archivo
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "pivot_metodos_pago_textiles.xlsx";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Pivot por Método de Pago</Typography>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
        >
          Descargar Excel
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1976d2" }}>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                MÉTODO DE PAGO
              </TableCell>
              <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                REVENUE CON IVA
              </TableCell>
              <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                REVENUE SIN IVA
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pivotData.map((row, index) => (
              <TableRow key={index} hover>
                <TableCell>{row.payment_method}</TableCell>
                <TableCell align="right">
                  {formatCurrency(row.revenue_con_iva)}
                </TableCell>
                <TableCell align="right">
                  {formatCurrency(row.revenue_sin_iva)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ backgroundColor: "#1976d2" }}>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                TOTAL
              </TableCell>
              <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                {formatCurrency(totals.revenue_con_iva)}
              </TableCell>
              <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                {formatCurrency(totals.revenue_sin_iva)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
